import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';

import { useAuth } from '../auth/AuthenticationContext.jsx';
import AppMenu from '../navigation/AppMenu.jsx';
import ControlErrorBanner from '../controls/ControlErrorBanner.jsx';
import HomeSelector from '../dashboard/HomeSelector.jsx';
import { setSelectedHomeId } from '../session/selectedHome.js';
import { useHomeRouteContext } from '../navigation/HomeRoute.jsx';
import { homeActionPath, homePath } from '../navigation/paths.js';
import ActionPanel from '../components/ActionPanel.jsx';
import ZoneTile from '../property/ZoneTile.jsx';
import GeolocationTile from '../property/GeolocationTile.jsx';
import { HomeHolidayBanner } from '../components/HolidayBanner.jsx';
import NonConnectedNotice from '../property/NonConnectedNotice.jsx';
import { PropertySkeletonBody } from './PropertySkeleton.jsx';
import {
  homeHolidayStatus,
  sameHomeHolidayStatus,
} from '../controls/holidayStatus.js';
import { DataIcon, HolidayIcon } from '../components/icons.jsx';
import useScreen from '../hooks/useScreen.js';

// Property (home) screen — the app-style landing page.
//
// Layout (locked with the product owner): a home selector (only when the account
// has more than one home), then the tile grid — one tile per zone (heating/DHW)
// and, for a Halo classic home, the app's geolocation status tile after them —
// then a horizontal row of the two property-level actions — Holiday Mode and
// Data. Pressing a zone tile opens its zone screen; the per-zone controls (the
// Radial + the dedicated action screens) live there, not here. The geolocation
// tile is status-only (see GeolocationTile).
//
// The header leads with the menu button (the app's burger, which only the
// Property screen carries — every other screen has a back link in that corner
// instead); its drawer holds the Help link and Logout (see navigation/AppMenu).
//
// The home list is read from the raw homeList.homes slice (not getHomes, which
// rebuilds a fresh array and churns useSelector); it already includes both device
// families and is sorted by the reducer. Per-zone reads go through the darwin_core
// selectors inside each tile.
//
// WHICH home: the one in the URL. This screen is the index of /property/:homeId, so it
// takes the homeId HomeRoute has already validated against the account (outlet
// context). The home is part of the address rather than shared client state, so
// each tab / bookmark / history entry owns its own context and switching home in
// one tab cannot change what another tab shows. The only thing kept outside the
// URL is the "last looked at" preference, written here and read solely by the
// root landing (PropertyLanding) to pick where a bare `/` entry goes. The landing
// also renders this screen with NO home context for the two states that have no
// home to address: before the account is seeded, and an account with no homes.
export default function PropertyScreen() {
  const headingRef = useScreen('Property');
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = user?.given_name;
  const homes = useSelector((state) => state.homeList.homes);
  const hadFirstHello = useSelector((state) => state.homeList.hadFirstHello);
  const { homeId = null } = useHomeRouteContext() ?? {};
  const selectedHome = homes.find((home) => home.id === homeId);

  // Remember the home being viewed as the landing default for the next bare entry.
  useEffect(() => {
    if (homeId) {
      setSelectedHomeId(homeId);
    }
  }, [homeId]);

  // The Holiday action's sub-label. Read per thermostat (homeHolidayStatus), not
  // getNearestHolidayEndTime, so a holiday the device reports with no end time
  // still shows as active and the date agrees with the notice beside it. Read
  // unconditionally (hooks); null-safe without a home.
  const holiday = useSelector(
    (state) => homeHolidayStatus(state, homeId),
    sameHomeHolidayStatus,
  );

  // Drop malformed zones (a known type that arrived with no [prtId, endpointId]
  // tuple — the same untrusted-data guard the read model applies) so a tile is
  // never built from an undefined tuple.
  const zones = (selectedHome?.zones ?? []).filter((zone) =>
    Array.isArray(zone?.zoneId),
  );

  // The app's rule for its geolocation tile (Property.js): a Halo classic home —
  // neither Halo Air nor a Logic device — gets one after its zone tiles, whatever
  // its geolocation state (a Disabled tile still shows). Mirrored exactly, through
  // the same selectors, so both clients agree for every home. Both are false
  // without a home, so the tile also needs a home to name.
  const isHaloAir = useSelector((state) =>
    selectors.getIsHaloAir(state, homeId),
  );
  const isLogicDevice = useSelector((state) =>
    selectors.getIsLogicDevice(state, homeId),
  );
  const showGeolocationTile =
    selectedHome !== undefined && !isHaloAir && !isLogicDevice;

  // A home whose thermostat is a non-connected version has no cloud licence, so
  // there is nothing here to control: the notice replaces the tiles and actions,
  // as the app replaces its Property screen (isCloudSupported → cloudSupported →
  // the API's connectedDevice). The selector and the home name stay above it —
  // see NonConnectedNotice on why the web cannot take the whole screen. Read
  // unconditionally (hooks) and false without a home, so it is paired with an
  // actual home below.
  const cloudSupported = useSelector((state) =>
    selectors.isCloudSupported(state, homeId),
  );
  const nonConnected = selectedHome !== undefined && !cloudSupported;

  // Switching home is a navigation: the chosen home's page gets its own URL and
  // history entry, so browser Back returns to the previous home.
  const handleSelect = (id) => {
    navigate(homePath(id));
  };

  let holidaySub = 'Inactive';
  if (holiday.away.length > 0) {
    holidaySub = holiday.nearestEnd
      ? `Active until ${new Date(holiday.nearestEnd).toLocaleDateString(
          'en-GB',
          { day: 'numeric', month: 'long' },
        )}`
      : 'Active';
  }

  let body;
  if (!hadFirstHello) {
    // Bones where the name, tiles and actions will land (the app's isLoading
    // shape); the message is for assistive tech.
    body = (
      <>
        <p role="status" aria-busy="true" className="visually-hidden">
          Loading your properties…
        </p>
        <PropertySkeletonBody />
      </>
    );
  } else if (homes.length === 0) {
    body = (
      <p className="dashboard-status" role="status">
        No properties are associated with this account.
      </p>
    );
  } else {
    body = (
      <>
        {homes.length > 1 && (
          <HomeSelector
            homes={homes}
            selectedId={homeId}
            onSelect={handleSelect}
          />
        )}
        <h2 className="dashboard-home-name">
          {selectedHome?.friendlyName || homeId}
        </h2>
        {nonConnected ? (
          <NonConnectedNotice />
        ) : (
          <>
            {/* Why the tiles below are locked, and the way out. Holiday is per
            thermostat, so this names the zones that are away when only some are. */}
            <HomeHolidayBanner
              homeId={homeId}
              detail="Cancel Holiday to open the locked zones."
            />
            <section aria-label="Zones">
              {zones.length === 0 && (
                <p className="dashboard-status" role="status">
                  No zones are reporting for this property yet.
                </p>
              )}
              {/* The geolocation tile does not depend on the zones, so a Halo classic
              home with none reporting still shows it (as in the app). */}
              {(zones.length > 0 || showGeolocationTile) && (
                <ul className="zone-tile-row">
                  {zones.map((zone) => (
                    <li
                      key={zone.zoneId.join('_')}
                      className="zone-tile-row__item"
                    >
                      <ZoneTile homeId={homeId} zoneId={zone.zoneId} />
                    </li>
                  ))}
                  {showGeolocationTile && (
                    <li key="geolocation" className="zone-tile-row__item">
                      <GeolocationTile homeId={homeId} />
                    </li>
                  )}
                </ul>
              )}
            </section>
            <nav className="property-action-row" aria-label="Property actions">
              <ActionPanel
                to={homeActionPath(homeId, 'holiday')}
                icon={<HolidayIcon />}
                title="Holiday Mode"
                sub={holidaySub}
              />
              <ActionPanel
                to={homeActionPath(homeId, 'data')}
                icon={<DataIcon />}
                title="Data"
                sub="Device data"
              />
            </nav>
          </>
        )}
      </>
    );
  }

  return (
    <main className="app-shell dashboard">
      <header className="app-header">
        <AppMenu />
        <div className="greeting">
          <h1
            className="greeting__title"
            id="main-content"
            tabIndex={-1}
            ref={headingRef}
          >
            Hello
            {firstName ? (
              <>
                , <span className="greeting__name">{firstName}</span>
              </>
            ) : null}
          </h1>
          <p className="greeting__subtitle">Welcome to your heating portal</p>
        </div>
      </header>
      {/* Connection status is now rendered once for every screen by GatedLayout.
          Loud command failures (holiday / publish) surface here, above the
          content, so they are seen wherever the user is. */}
      <ControlErrorBanner />
      {body}
    </main>
  );
}
